import {
  Badge,
  StatusBadge as SharedStatusBadge,
  TaxId,
} from '@vrplatform/components';
import type { gqlV2 } from '@vrplatform/data';
import {
  BriefcaseIcon,
  CreditCardOutcomeIcon,
  EmailIcon,
  HashtagIcon,
  LoaderIcon,
  PhoneIcon,
  PinIcon,
  UserIcon,
} from '@vrplatform/icons';
import { Icon } from '@vrplatform/icons';
import { useAppName } from '@vrplatform/theme';
import { type Maybe, day } from '@vrplatform/utils';
import { Center, Group, Tooltip, useMantineTheme } from '@mantine/core';
import { Text } from '@mantine/core';
import type { activeStatus_enum } from '@vrplatform/graphql';
import { formatAddress } from '@vrplatform/ui-common';
import type { ComponentProps } from 'react';
import { DrawerInfoCard } from '../../_components';
import type { OwnerDetail } from '../useOwnerDetailDrawerQuery';

type Props = {
  owner: {
    companyType: string | undefined;
    status: Maybe<activeStatus_enum>;
    pmsStatus: Maybe<activeStatus_enum>;
    address: OwnerDetail['address'];
    email: Maybe<string>;
    phone: Maybe<string>;
    taxId: Maybe<string>;
    payoutAccountName: Maybe<string>;
    userAccess: OwnerDetail['userAccess'];
  };
  defaultPayoutAccountName: Maybe<string>;
};

export const OwnerInfoCard = ({ owner, defaultPayoutAccountName }: Props) => {
  return (
    <DrawerInfoCard
      labelWidth={130}
      rows={[
        {
          icon: LoaderIcon,
          title: 'Status',
          text: (owner.status === 'inactive' ||
            owner.pmsStatus === 'inactive') && (
            <OwnerRecordStatusBadge {...owner} />
          ),
        },
        {
          icon: BriefcaseIcon,
          title: 'Type',
          text: owner.companyType,
        },
        {
          icon: HashtagIcon,
          title: 'Tax ID',
          text: owner.taxId?.trim() ? <TaxId taxId={owner.taxId} /> : null,
        },
        {
          icon: EmailIcon,
          title: 'Email',
          text: owner.email || '-',
        },
        {
          icon: PhoneIcon,
          title: 'Phone',
          text: owner.phone || '-',
        },
        {
          icon: PinIcon,
          title: 'Address',
          text: formatAddress(owner.address) || '-',
        },
        {
          icon: CreditCardOutcomeIcon,
          title: 'Payout account',
          text:
            owner.payoutAccountName ||
            (defaultPayoutAccountName ? (
              <Text>
                Default
                <Text span c="gray">
                  {' '}
                  ・ {defaultPayoutAccountName}
                </Text>
              </Text>
            ) : (
              <Badge color="red">Missing team default payout account</Badge>
            )),
        },
        {
          icon: UserIcon,
          title: 'Owner portal',
          text: <OwnerPortalAccessBadge userAccess={owner.userAccess} />,
        },
      ]}
    />
  );
};

export const OwnerPortalAccessBadge = ({
  userAccess,
}: {
  userAccess: OwnerDetail['userAccess'];
}) => {
  const lastInvitedAt = userAccess?.lastInvitedAt;

  const label = lastInvitedAt
    ? `Last invited on ${day(lastInvitedAt).format('LL')}`
    : null;

  if (!userAccess || userAccess.status === 'inactive')
    return (
      <SharedStatusBadge label="Not invited" status="missing" tooltip={label} />
    );

  if (userAccess.status === 'active')
    return (
      <SharedStatusBadge label="Active" status="success" tooltip={label} />
    );

  // remaining is unconfirmed
  if (lastInvitedAt && day(lastInvitedAt).isBefore(day().subtract(7, 'day')))
    return (
      <SharedStatusBadge label="Expired" status="expired" tooltip={label} />
    );

  return <SharedStatusBadge label="Pending" status="pending" tooltip={label} />;
};

const OwnerRecordStatusBadge = (data: {
  pmsStatus: Maybe<gqlV2.activeStatus_enum>;
  status: Maybe<gqlV2.activeStatus_enum>;
}) => {
  if (data.pmsStatus === 'inactive' && !data.status) return <PmsStatus />;

  return <OwnerStatus {...data} />;
};

const PmsStatus = () => {
  return (
    <Badge
      color="orange"
      leftIcon={
        <Icon
          icon="AlertTriangleIcon"
          size={12}
          color={(theme) => theme.colors.orange[8]}
        />
      }
    >
      Disabled in PMS
    </Badge>
  );
};

const OwnerStatus = (data: ComponentProps<typeof OwnerRecordStatusBadge>) => {
  const { colors } = useMantineTheme();
  const { appName } = useAppName();

  const isDisabledInPms = data.pmsStatus === 'inactive';

  if (data.status === 'inactive') {
    return (
      <Group wrap="nowrap" gap={'xs'}>
        {isDisabledInPms && (
          <Tooltip label="Archived in PMS" withinPortal withArrow>
            <Center>
              <Icon
                icon="AlertTriangleIcon"
                size={12}
                color={colors.orange[7]}
              />
            </Center>
          </Tooltip>
        )}

        <SharedStatusBadge label="Archived" status="archived" />
      </Group>
    );
  }

  return (
    <Group wrap="nowrap" gap={'xs'}>
      {isDisabledInPms && (
        <Tooltip label="Archived in PMS" withinPortal withArrow>
          <Center>
            <Icon icon="AlertTriangleIcon" size={12} color={colors.orange[7]} />
          </Center>
        </Tooltip>
      )}

      <SharedStatusBadge label={`Active on ${appName}`} status="success" />
    </Group>
  );
};
